/* ============================================================================
   PREDICTIVE SEARCH — InnovaZone
   Live product suggestions while typing in the header search input.
   Uses Shopify Predictive Search API (/search/suggest.json).
============================================================================ */

(function () {
  'use strict';

  const form = document.querySelector('[data-predictive-search-form]');
  if (!form) return;

  const input        = form.querySelector('[data-predictive-search-input]');
  const resultsBox   = form.querySelector('[data-predictive-search-results]');
  const list         = form.querySelector('[data-search-list]');
  const loadingEl    = form.querySelector('[data-search-loading]');
  const emptyEl      = form.querySelector('[data-search-empty]');
  const allLink      = form.querySelector('[data-search-all]');
  if (!input || !resultsBox || !list) return;

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 220;
  const RESULTS_LIMIT = 6;

  let debounceTimer = null;
  let currentController = null;
  let activeIndex = -1;
  let activeItems = [];

  /* ---------- API ---------- */
  function fetchResults(query) {
    if (currentController) currentController.abort();
    currentController = new AbortController();

    const url = '/search/suggest.json' +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=' + RESULTS_LIMIT +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,vendor,body';

    return fetch(url, { signal: currentController.signal })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        return products;
      });
  }

  /* ---------- Render ---------- */
  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str.replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function formatPrice(product) {
    // Shopify suggest.json returns price as string with currency symbol already
    if (product.price) return product.price;
    if (typeof product.price_min === 'number') return '₡' + product.price_min.toLocaleString('es-CR');
    return '';
  }

  function renderResults(products, query) {
    list.innerHTML = '';

    if (products.length === 0) {
      list.hidden = true;
      emptyEl.hidden = false;
      allLink.hidden = true;
      activeItems = [];
      return;
    }

    emptyEl.hidden = true;
    list.hidden = false;

    products.forEach(function (p) {
      const li = document.createElement('li');
      li.className = 'search__result';
      li.setAttribute('role', 'option');

      const img = p.image
        ? '<img src="' + escapeHtml(p.image) + '" alt="" loading="lazy" width="48" height="48">'
        : '<span class="search__result-img--placeholder" aria-hidden="true"></span>';

      const compareAt = (p.compare_at_price && p.compare_at_price !== p.price)
        ? '<span class="search__result-was">' + escapeHtml(p.compare_at_price) + '</span>'
        : '';

      li.innerHTML =
        '<a href="' + escapeHtml(p.url) + '" class="search__result-link" tabindex="-1">' +
          '<div class="search__result-img">' + img + '</div>' +
          '<div class="search__result-body">' +
            '<span class="search__result-title">' + escapeHtml(p.title) + '</span>' +
            '<span class="search__result-price">' +
              '<strong>' + escapeHtml(formatPrice(p)) + '</strong>' +
              compareAt +
            '</span>' +
          '</div>' +
        '</a>';

      list.appendChild(li);
    });

    // "Ver todos los resultados" link
    allLink.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
    allLink.hidden = false;

    activeItems = Array.from(list.querySelectorAll('.search__result-link'));
    activeIndex = -1;
  }

  function showResults() {
    resultsBox.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function hideResults() {
    resultsBox.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
    activeItems.forEach(function (a) { a.classList.remove('is-active'); });
  }

  /* ---------- Input handler ---------- */
  function onInput() {
    const query = input.value.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      hideResults();
      return;
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      loadingEl.hidden = false;
      list.hidden = true;
      emptyEl.hidden = true;
      allLink.hidden = true;
      showResults();

      fetchResults(query)
        .then(function (products) {
          loadingEl.hidden = true;
          renderResults(products, query);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          loadingEl.hidden = true;
          list.hidden = true;
          emptyEl.hidden = false;
        });
    }, DEBOUNCE_MS);
  }

  /* ---------- Keyboard navigation ---------- */
  function setActive(index) {
    if (activeItems.length === 0) return;
    if (index < 0) index = activeItems.length - 1;
    if (index >= activeItems.length) index = 0;
    activeIndex = index;
    activeItems.forEach(function (a, i) {
      a.classList.toggle('is-active', i === activeIndex);
      if (i === activeIndex) {
        a.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  input.addEventListener('keydown', function (e) {
    if (resultsBox.hidden) {
      if (e.key === 'ArrowDown' && input.value.trim().length >= MIN_QUERY_LENGTH) {
        showResults();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeItems[activeIndex]) {
        e.preventDefault();
        window.location.href = activeItems[activeIndex].href;
      }
      // else let the form submit normally
    } else if (e.key === 'Escape') {
      hideResults();
      input.blur();
    }
  });

  /* ---------- Events ---------- */
  input.addEventListener('input', onInput);
  input.addEventListener('focus', function () {
    if (input.value.trim().length >= MIN_QUERY_LENGTH && (activeItems.length > 0 || emptyEl.hidden === false)) {
      showResults();
    }
  });

  // Close on outside click
  document.addEventListener('click', function (e) {
    if (!form.contains(e.target)) hideResults();
  });

  // Re-show on input click if there are results
  input.addEventListener('click', function () {
    if (input.value.trim().length >= MIN_QUERY_LENGTH && activeItems.length > 0) {
      showResults();
    }
  });

})();
